import React, { useCallback, useMemo } from 'react';
import { toast, ToastContainer, ToastOptions } from 'react-toastify';
import { Alert, AlertColor } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';
import './notification.css';

// Notification types
export type NotificationStatus = 'success' | 'error' | 'warning' | 'info';

// Custom toast component using MUI Alert
const ToastContent: React.FC<{ title?: string; message: string; status: NotificationStatus }> = ({
  title,
  message,
  status,
}) => {
  const getAlertSeverity = (status: NotificationStatus): AlertColor => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  };

  return (
    <Alert
      severity={getAlertSeverity(status)}
      variant="filled"
      onClose={() => toast.dismiss()}
      sx={{
        width: '100%',
        minWidth: '300px',
        maxWidth: '500px',
        '& .MuiAlert-icon': {
          display: 'none', // Hide the default MUI icon
        },
        '& .MuiAlert-action': {
          padding: 0,
        },
      }}
    >
      {title && <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{title}</div>}
      <div>{message}</div>
    </Alert>
  );
};

// Custom hook for notifications
export const useNotifications = () => {
  const show = (params: {
    title?: string;
    message: string;
    status?: NotificationStatus;
    autoHideDuration?: number;
  }) => {
    const { title, message, status = 'info', autoHideDuration = 1500 } = params;

    const toastOptions: ToastOptions = {
      position: 'top-right',
      autoClose: autoHideDuration,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
      closeButton: false, // Hide the default close button
      icon: false, // Hide the default icon
    };

    const toastContent = <ToastContent title={title} message={message} status={status} />;

    switch (status) {
      case 'success':
        toast.success(toastContent, toastOptions);
        break;
      case 'error':
        toast.error(toastContent, toastOptions);
        break;
      case 'warning':
        toast.warning(toastContent, toastOptions);
        break;
      case 'info':
      default:
        toast.info(toastContent, toastOptions);
        break;
    }
  };

  const hide = useCallback((toastId: string | number) => {
    toast.dismiss(toastId);
  }, []);

  const hideAll = useCallback(() => {
    toast.dismiss();
  }, []);

  return useMemo(() => ({ show, hide, hideAll }), [show, hide, hideAll]);
};

// Toast container component with custom styling
export const NotificationContainer: React.FC = () => {
  return (
    <ToastContainer
      position="top-right"
      autoClose={1500}
      hideProgressBar={true}
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
      limit={5}
      style={{ zIndex: 9999 }}
      toastClassName="custom-toast"
      closeButton={false}
    />
  );
};

// Export the hook as default
export default useNotifications;
